import { AppThunk, RENAME_FOLDER,  FETCH_STRUCTURE_SUCCESS, FILE_IMPORT_CONNECTED, FILE_IMPORT_FAILED, FILE_IMPORT_STEPS, FILE_IMPORT_STEP, FILE_IMPORT_COMPLETED, FILE_IMPORT_CONNECTING, FILE_IMPORT_DONE } from '../types';
import apis, { tokenProvider, fetchWithSSE } from '../../utils/apis';
import { showFeedbackModal } from './UserFeedbackActions';
import { FileProps, FolderProps } from '../../components/Files/documents/types';
import { handleApiError } from './utils/utils';
import { ThunkDispatch } from 'redux-thunk';
import { RootState } from '..';
import { AnyAction } from 'redux';
import { config } from '../../utils/config';

interface FetchStructureSuccessPayload {
  structure: FolderProps[];
  flatFiles: FileProps[];
}

interface Collaborator {
  user_uuid: string;
  email: string;
  name: string;
  access: 'view' | 'edit' | 'admin';
  role: string;
  initials: string;
  avatar_url?: string;
}

export const setFolderName = (newFolderName: string) => ({
  type: RENAME_FOLDER,
  payload: newFolderName
});

/**
 * Fetch all folders the user has access to for hierarchical dropdown.
 * Returns flat list with id, label, parent_id, collaborators.
 */
export const fetchFolderTree = (): AppThunk<Promise<FolderTreeItem[]>> => async (dispatch) => {
  try {
    const response = await apis.get('/files/get_folder_tree');
    if (response && response.status === 200) {
      return response.data?.folders || [];
    }
    return [];
  } catch (error: any) {
    console.error('[fetchFolderTree] Error:', error);
    return [];
  }
};

export interface FolderTreeItem {
  id: string;
  label: string;
  parent_id: string | null;
  collaborators: Collaborator[];
}

/**
 * Fetch collaborators for a specific folder
 */
export const getFolderCollaborators = (folderID: string): AppThunk<Promise<Collaborator[]>> => async (dispatch) => {
  try {
    const response = await apis({
      method: 'GET',
      url: `/files/get_file_and_sub_folder?folder_id=${folderID}&page=1&limit=1`
    });

    if (response && response.status === 200) {
      const collaborators = response.data?.collaborators || [];
      return Promise.resolve(collaborators);
    } else {
      console.warn('[getFolderCollaborators] Unexpected response status:', response?.status);
      return Promise.resolve([]);
    }
  } catch (error: any) {
    console.error('[getFolderCollaborators] Error fetching folder collaborators:', error);
    return Promise.resolve([]); // Return empty array on error instead of throwing
  }
};

const fetchStructureSuccess = (payload: FetchStructureSuccessPayload): { type: string; payload: FetchStructureSuccessPayload } => ({
  type: FETCH_STRUCTURE_SUCCESS,
  payload: payload,
});



export const renameFolder = (folderId: string, newFolderName: string): AppThunk<Promise<FolderProps[]>> => async (dispatch) => {
  try {
    const folderResponse = await apis({
      method: "POST",
      url: '/files/rename_folder',
      data: { FolderID: folderId, NewFolderName: newFolderName }
    });

    if (folderResponse.status === 200) {
      let folderName = folderResponse.data;
      dispatch(setFolderName(folderName));
      dispatch(showFeedbackModal({
        showModal: true,
        message: ("Folder renamed"),
        modalType: 'success',
        duration: 3000
      }));
      return Promise.resolve(folderName);
    } else {
      handleApiError(folderResponse, dispatch)
      return Promise.reject(new Error('Failed to rename folder'));
    }
  } catch (error) {
    handleApiError(error, dispatch)
    return Promise.reject(error);
  }
};

export const deleteFolder = (folderIds?: string[], callback?: () => void): AppThunk<Promise<boolean>> => async (dispatch) => {
  try {
    const response = await apis({
      method: 'POST',
      url: "files/delete_folder",
      data: { FolderIDs: folderIds }
    });

    if (response && response.status === 200) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: 'Folder deleted',
        modalType: 'success',
        duration: 3000
      }));
      if (callback) callback();
      return true
    } else {
      handleApiError(response, dispatch)
    }
  } catch (error) {
    handleApiError(error, dispatch)
  }
  return false;
};


export const fetchStructure = (params: {searchTerm?: string; filters?: {[key:string]: string[]}; sortBy?: string; sortOrder?: string,page?: number; limit?: number;} = {}): AppThunk<Promise<any>> => async (dispatch) => {
  try {
    const { searchTerm = '', filters = {}, sortBy = '', sortOrder = 'asc', page= 1, limit = 50 } = params;
    const filtersJson = JSON.stringify(filters);

    const response = await apis.get('/files/get_file_structure', {
      params: { searchTerm, filters: filtersJson, sortBy, sortOrder, page, limit }
    });

    if (response && response.status === 200) {
      const structureData = response.data && response.data.folders ? response.data.folders : [];
      const filesData = response.data && response.data.files ? response.data.files : [];
      dispatch(fetchStructureSuccess({ structure: structureData, flatFiles: filesData }));
      return {
        folders: structureData,
        files: filesData,
        pagination: response.data?.pagination || { page, limit, total_items: 0, has_next: false, shown_items: 0 }
      };
    } else {
      handleApiError(response, dispatch);
      return { folders: [], files: [], pagination: { page, limit, total_items: 0, has_next: false, shown_items: 0 } };
    }
  } catch (error: any) {
    handleApiError(error, dispatch);
    return { folders: [], files: [], pagination: { page: 1, limit: 50, total_items: 0, has_next: false, shown_items: 0 } };
  }
};

export const fetchSubFolderAndFiles = (
  params: { 
    folderID: string; 
    searchTerm?: string; 
    filters?: { [key: string]: string[] }; 
    sortBy?: string; 
    sortOrder?: string;
    limit?: number;
    page?:number;
    folderName?: string;
  }
): AppThunk<Promise<{ subfolders: any[]; files: any[], pagination: any } | undefined>> => async (dispatch) => {
  try {
    const { folderID, searchTerm = '', filters = {}, sortBy = '', sortOrder = 'asc', limit=50, page=1,folderName} = params;

    const filtersJson = JSON.stringify(filters);

    const response = await apis.get('/files/get_file_and_sub_folder', {
      params: { folderID, searchTerm, filters: filtersJson, sortBy, sortOrder,limit, page,folderName}
    });

    if (response.status === 200) {
      const { subfolders = [], files = [], pagination } = response.data;  

      return { subfolders, files ,pagination };
    } else {
      handleApiError(response, dispatch);
      return undefined; 
    }
  } catch (error: any) {
    handleApiError(error, dispatch);
    return undefined; 
  }
};



export const deleteFile = (FileIDs: string[], callback?: () => void, folderID?: string): AppThunk<Promise<boolean>> => async (dispatch) => {
  try {
    const response = await apis({
      method: 'POST',
      url: "/files/delete_file",
      data: { FileIDs, FolderID: folderID || undefined }
    });

    if (response && response.status === 200) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: response.data.message || 'File(s) deleted',
        modalType: 'success',
        duration: 3000
      }));
      if (callback) callback();
      return true
    } else {
      handleApiError(response, dispatch)
    }
  } catch (error) {
    handleApiError(error, dispatch)
  }
  return false;
};


export const importFilesSSE =
  (data: Record<string, any>, files?: File[] | { [key: string]: File }) =>
  async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    return new Promise<any>(async (resolve, reject) => {
      dispatch({ type: FILE_IMPORT_CONNECTING });

      try {
        const fd = new FormData();

        Object.entries<any>(data || {}).forEach(([k, v]) => {
          if (v === undefined || v === null) return;
          if (typeof v === "object" && !(v instanceof File)) {
            fd.append(k, JSON.stringify(v));
          } else {
            fd.append(k, String(v));
          }
        });

        if (Array.isArray(files)) {
          files.forEach((f) => f && fd.append("files[]", f, f.name));
        } else if (files && typeof files === "object") {
          Object.values(files).forEach((f) => f && fd.append("files[]", f, f.name));
        }

        const resp = await fetchWithSSE(`${config.baseUrl}/files/import_files_sse`, {
          method: "POST",
          body: fd,
        });

        if (!resp.ok) {
          const msg = `HTTP ${resp.status}`;
          dispatch({ type: FILE_IMPORT_FAILED, payload: { error: msg } });
          return reject(new Error(msg));
        }
        if (!resp.body) {
          const msg = "No SSE body returned";
          dispatch({ type: FILE_IMPORT_FAILED, payload: { error: msg } });
          return reject(new Error(msg));
        }

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let resolved = false;

        const processEvent = (raw: string) => {
          if (!raw || !raw.trim()) return;

          let event = "message";
          let dataStr = "";

          raw.split("\n").forEach((line) => {
            if (line.startsWith("event:")) event = line.slice(6).trim();
            if (line.startsWith("data:")) dataStr += line.slice(5).trim();
          });

          if (event === "done") {
            dispatch({ type: FILE_IMPORT_DONE });
            return;
          }

          try {
            const parsed = dataStr ? JSON.parse(dataStr) : null;

            switch (event) {
              case "connected":
                dispatch({ type: FILE_IMPORT_CONNECTED, payload: parsed });
                break;

              case "steps":
                dispatch({ type: FILE_IMPORT_STEPS, payload: parsed });
                break;

              case "step":
                dispatch({ type: FILE_IMPORT_STEP, payload: parsed });
                break;

              case "completed":
                dispatch({ type: FILE_IMPORT_COMPLETED, payload: parsed });
                resolved = true;
                resolve(parsed || { ok: false });
                break;

              case "error":
                dispatch({ type: FILE_IMPORT_FAILED, payload: parsed });
                resolved = true;
                reject(parsed);
                break;

              default:
                break;
            }
          } catch (parseErr) {
            console.warn("[importFilesSSE] Failed to parse SSE event:", event, parseErr);
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            // Process any remaining data in the buffer
            if (buffer.trim()) {
              processEvent(buffer.trim());
            }
            break;
          }

          buffer += decoder.decode(value, { stream: true });

          // Split on double newline (SSE event boundary)
          const events = buffer.split("\n\n");
          // Keep the last segment — it may be an incomplete event
          buffer = events.pop() || "";

          for (const rawEvent of events) {
            processEvent(rawEvent);
          }
        }

        // Safety net: if the stream closed without a completed/error event, resolve
        if (!resolved) {
          console.warn("[importFilesSSE] Stream ended without completed/error event — resolving with fallback.");
          dispatch({ type: FILE_IMPORT_COMPLETED, payload: { ok: true, message: "Import stream ended", folderId: data.FolderID } });
          resolve({ ok: true, streamEnded: true });
        }
      } catch (err: any) {
        const msg = String(err?.message || err);
        dispatch({ type: FILE_IMPORT_FAILED, payload: { error: msg } });
        reject(err);
      }
    });
  };

export const createFolderAction = (data: {
  FolderName: string;
  ParentFolderID?: string;
  SharedWith?: string[];
  collaborators?: Array<{ user_id: number | string; access: 'edit' | 'view' | 'admin' }>;
}): AppThunk<Promise<any>> => async (dispatch) => {
  try {
    const response = await apis({
      method: 'POST',
      url: '/files/create_folder',
      data: {
        FolderName: data.FolderName,
        ParentFolderID: data.ParentFolderID || undefined,
        collaborators: data.collaborators || undefined,
        // Keep backward compatibility with SharedWith if collaborators is not provided
        ShareWithUserIDs: data.SharedWith && !data.collaborators ? data.SharedWith : [],
      }
    });

    if (response && response.status === 201) {
      // Return the data without dispatching feedback here
      // Let the component handle feedback display
      return response.data;
    } else {
      handleApiError(response, dispatch);
      throw new Error('Failed to create folder');
    }
  } catch (error: any) {
    handleApiError(error, dispatch);
    throw error;
  }
};


// =====================================================================
// TRASH / RECYCLE BIN ACTIONS
// =====================================================================

export const fetchTrash = (params?: { page?: number; limit?: number; sortBy?: string; sortOrder?: string; searchTerm?: string }): AppThunk<Promise<any>> => async (dispatch) => {
  try {
    const response = await apis.get('/files/trash', { params });
    if (response?.data?.success) {
      return response.data;
    }
    return { success: false, folders: [], files: [], pagination: {} };
  } catch (error: any) {
    handleApiError(error, dispatch);
    return { success: false, folders: [], files: [], pagination: {} };
  }
};

export const restoreFromTrash = (folderIds: string[], fileIds: string[]): AppThunk<Promise<boolean>> => async (dispatch) => {
  try {
    const response = await apis.post('/files/trash/restore', {
      FolderIDs: folderIds,
      FileIDs: fileIds
    });

    if (response?.data?.success) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: 'Items restored successfully',
        modalType: 'success',
        duration: 3000
      }));
      return true;
    }

    handleApiError(response, dispatch);
    return false;
  } catch (error: any) {
    handleApiError(error, dispatch);
    return false;
  }
};

export const permanentDeleteFromTrash = (folderIds: string[], fileIds: string[]): AppThunk<Promise<boolean>> => async (dispatch) => {
  try {
    const response = await apis.post('/files/trash/permanent_delete', {
      FolderIDs: folderIds,
      FileIDs: fileIds
    });

    if (response?.data?.success) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: 'Items permanently deleted',
        modalType: 'success',
        duration: 3000
      }));
      return true;
    }

    handleApiError(response, dispatch);
    return false;
  } catch (error: any) {
    handleApiError(error, dispatch);
    return false;
  }
};

export const emptyTrash = (): AppThunk<Promise<boolean>> => async (dispatch) => {
  try {
    const response = await apis.post('/files/trash/empty');

    if (response?.data?.success) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: 'Trash emptied successfully',
        modalType: 'success',
        duration: 3000
      }));
      return true;
    }

    handleApiError(response, dispatch);
    return false;
  } catch (error: any) {
    handleApiError(error, dispatch);
    return false;
  }
};

// =====================================================================
// RETENTION SETTINGS ACTIONS
// =====================================================================

export const fetchTrashImpact = (retentionDays: number): AppThunk<Promise<any>> => async (dispatch) => {
  try {
    const response = await apis.get('/files/trash/impact', { params: { retention_days: retentionDays } });
    if (response?.data?.success) {
      return response.data;
    }
    return null;
  } catch (error: any) {
    return null;
  }
};

export const fetchRetentionSettings = (): AppThunk<Promise<number>> => async (dispatch) => {
  try {
    const response = await apis.get('/files/settings/retention');
    if (response?.data?.success) {
      return response.data.retention_days || 30;
    }
    return 30;
  } catch (error: any) {
    handleApiError(error, dispatch);
    return 30;
  }
};

export const updateRetentionSettings = (retentionDays: number): AppThunk<Promise<boolean>> => async (dispatch) => {
  try {
    const response = await apis.put('/files/settings/retention', {
      retention_days: retentionDays
    });

    if (response?.data?.success) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: 'Retention settings updated successfully',
        modalType: 'success',
        duration: 3000
      }));
      return true;
    }

    handleApiError(response, dispatch);
    return false;
  } catch (error: any) {
    handleApiError(error, dispatch);
    return false;
  }
};


// =====================================================================
// DOCUMENT TAGGING ACTIONS
// =====================================================================

export const fetchTags = (): AppThunk<Promise<any[]>> => async (dispatch) => {
  try {
    const response = await apis.get('/files/tags');
    if (response?.data?.success) {
      return response.data.tags || [];
    }
    return [];
  } catch (error: any) {
    handleApiError(error, dispatch);
    return [];
  }
};

export const createTag = (name: string, color?: string): AppThunk<Promise<any>> => async (dispatch) => {
  try {
    const response = await apis.post('/files/tags', { name, color });
    if (response?.data?.success) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: 'Tag created',
        modalType: 'success',
        duration: 3000
      }));
      return response.data.tag;
    }
    handleApiError(response, dispatch);
    return null;
  } catch (error: any) {
    // Surface 409 duplicate as a specific error so callers can show inline feedback
    if (error?.response?.status === 409) {
      return { _error: 'duplicate', message: error.response?.data?.error || 'A tag with this name already exists' };
    }
    handleApiError(error, dispatch);
    return null;
  }
};

export const updateTag = (tagUuid: string, data: { name?: string; color?: string }): AppThunk<Promise<any>> => async (dispatch) => {
  try {
    const response = await apis.put(`/files/tags/${tagUuid}`, data);
    if (response?.data?.success) {
      return response.data.tag;
    }
    handleApiError(response, dispatch);
    return null;
  } catch (error: any) {
    handleApiError(error, dispatch);
    return null;
  }
};

export const deleteTag = (tagUuid: string): AppThunk<Promise<boolean>> => async (dispatch) => {
  try {
    const response = await apis.delete(`/files/tags/${tagUuid}`);
    if (response?.data?.success) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: 'Tag deleted',
        modalType: 'success',
        duration: 3000
      }));
      return true;
    }
    handleApiError(response, dispatch);
    return false;
  } catch (error: any) {
    handleApiError(error, dispatch);
    return false;
  }
};

export const assignTag = (
  tagId: number,
  action: 'add' | 'remove',
  fileId?: string,
  folderId?: string
): AppThunk<Promise<any[]>> => async (dispatch) => {
  try {
    const response = await apis.post('/files/tags/assign', {
      tag_id: tagId,
      action,
      file_id: fileId,
      folder_id: folderId
    });
    if (response?.data?.success) {
      return response.data.tags || [];
    }
    handleApiError(response, dispatch);
    return [];
  } catch (error: any) {
    handleApiError(error, dispatch);
    return [];
  }
};

// =====================================================================
// AUDIT LOG ACTIONS
// =====================================================================

export const fetchAuditLog = (
  entityType: 'file' | 'folder',
  entityId: string,
  page: number = 1,
  limit: number = 50
): AppThunk<Promise<any>> => async (dispatch) => {
  try {
    const response = await apis.get('/files/audit-log', {
      params: { entity_type: entityType, entity_id: entityId, page, limit }
    });
    if (response?.data?.success) {
      return {
        entries: response.data.entries || [],
        pagination: response.data.pagination || {}
      };
    }
    return { entries: [], pagination: {} };
  } catch (error: any) {
    handleApiError(error, dispatch);
    return { entries: [], pagination: {} };
  }
};
